import { useMemo } from 'react';

import { useAppSelector } from '@/store/hooks';
import type { LecturePreview } from '@/types/Lecture';
import {
  selectionPreviewFolder,
  type LectureTreeNodesById,
} from '@/utils/lectureTree';

const NO_NODES: LectureTreeNodesById = {};

/**
 * What a selection is called in the study start dialogs and the preview: one
 * lecture's name, or "N Lectures Selected". In a course planned with node kinds
 * a document is a part of a lecture, not a lecture: a selection that is exactly
 * one folder takes the folder's name, and any other says "N Items Selected".
 * Only such a selection reads the tree, so no other user re-renders on it.
 */
export const useLectureSelectionLabel = (
  selectedLectures: readonly Pick<LecturePreview, 'id' | 'name'>[],
  nounPlural: string
): string => {
  const hasKinds = useAppSelector((state) =>
    selectedLectures.some(
      ({ id }) => state.lectureTree?.nodesById[id]?.kind != null
    )
  );

  const nodesById = useAppSelector((state) =>
    hasKinds ? state.lectureTree.nodesById : NO_NODES
  );

  return useMemo(() => {
    if (selectedLectures.length === 1) return selectedLectures[0].name;

    const count = selectedLectures.length;
    if (!hasKinds) return `${count} ${nounPlural} Selected`;

    const folder = selectionPreviewFolder(
      selectedLectures.map(({ id }) => id),
      nodesById
    );

    return folder?.name ?? `${count} Items Selected`;
  }, [hasKinds, nodesById, nounPlural, selectedLectures]);
};
